import {
  AssignmentPeriodChainListResponseDto,
  AssignmentPeriodListResponseDto,
  PayOutProvisionRequestDto,
  UserPaidOutProvisionListResponseDto,
  UserProvisionHistoryListResponseDto,
  UserProvisionListPaginatedResponseDtoWithSelectedDateRange,
  UserProvisionListResponseDto,
  UserProvisionOrganizationsListResponseDto,
  UserProvisionOrganizationsResponseDto,
} from "@/lib/interfaces/bonus";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Calendar,
  CheckCircle2,
  ChevronDown,
  Clock,
  Euro,
  FileText,
  Info,
  MapPin,
  Plus,
  TrendingUp,
  Users,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { DateRange } from "react-day-picker";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";
import { useUser } from "../../../hooks/use-user";
import useApiRequest from "../../../lib/hooks/useRequest";
import { formatDate } from "../../../lib/hooks/utils";
import { UserRole } from "../../../lib/interfaces/user";
import { Button } from "../../../ui/components/ui/button";
import DataFetchParent from "../../../ui/components/ui/data-fetch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../../ui/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "../../../ui/components/ui/form";
import { Input } from "../../../ui/components/ui/input";
import { DatePickerWithRange } from "../../../ui/components/ui/range-date-picker";
import UserProfileRow from "./user-profile-row";

const BonusPayments = ({ textSearch }: { textSearch: string }) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [date, setDate] = useState<DateRange | undefined>();
  const [dateReload, setDateReload] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [data, setData] = useState<UserProvisionListResponseDto[]>([]);
  const { apiRequest } = useApiRequest();
  const { user } = useUser();
  const isAdmin = user?.userRole === UserRole.Admin || user?.userRole === UserRole.SuperAdmin;
  const [paginatedInfos, setPaginatedInfos] = useState<{ page: number; limit: number; hasNextPage: boolean }>({
    page: 1,
    limit: 6,
    hasNextPage: false,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [initLoad, setInitLoad] = useState(true);

  const getBonusPaymentsData = useCallback(
    async (date: DateRange | undefined, isLoadMore = false) => {
      if (isLoadMore) {
        setLoadingMore(true);
      } else {
        setLoading(true);
      }

      const currentPage = isLoadMore ? paginatedInfos.page + 1 : 1;
      let url = `provision/admin/internal-user-provisions?limit=${paginatedInfos.limit}&page=${currentPage}`;

      if (date?.from && date?.to) {
        url += `&startDate=${date.from?.toISOString()}&endDate=${date.to?.toISOString()}`;
      }

      if (textSearch) {
        url += `&textSearch=${textSearch}`;
      }

      const response = await apiRequest<UserProvisionListPaginatedResponseDtoWithSelectedDateRange>(url, "GET");

      if (response.data && response.data.users) {
        if (isLoadMore) {
          // Append new data to existing data
          setData((prevData) => [...prevData, ...response.data!.users.docs]);
        } else {
          // Replace data for initial load or filter changes
          setData(response.data.users.docs);
          const fromDate = new Date(response.data.startDate);
          const toDate = new Date(response.data.endDate);

          setDate({
            from: new Date(fromDate.toUTCString()),
            to: new Date(toDate.toUTCString()),
          });
          setDateReload(false);
        }

        setPaginatedInfos({
          page: response.data.users.page,
          limit: response.data.users.limit,
          hasNextPage: response.data.users.hasNextPage,
        });
      }

      if (isLoadMore) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
    },
    [apiRequest, textSearch, paginatedInfos.limit, paginatedInfos.page]
  );

  // Load more data when scrolling near bottom
  const loadMoreData = useCallback(() => {
    if (!loadingMore && paginatedInfos.hasNextPage) {
      getBonusPaymentsData(date, true);
    }
  }, [getBonusPaymentsData, date, loadingMore, paginatedInfos.hasNextPage]);

  // Scroll event handler for infinite scroll
  const handleScroll = useCallback(() => {
    if (!scrollContainerRef.current) return;

    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    const scrolledPercentage = (scrollTop + clientHeight) / scrollHeight;

    // Trigger load more when scrolled 80% of the way
    if (scrolledPercentage > 0.8) {
      loadMoreData();
    }
  }, [loadMoreData]);

  // Set up scroll listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      return () => scrollContainer.removeEventListener("scroll", handleScroll);
    }
  }, [handleScroll]);

  useEffect(() => {
    let dateToUse = date;
    if (initLoad) {
      const startDateParam = searchParams.get("startDate");
      const endDateParam = searchParams.get("endDate");

      if (startDateParam && endDateParam) {
        const startDate = new Date(startDateParam);
        const endDate = new Date(endDateParam);

        // Validate dates
        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          setInitLoad(false);
          dateToUse = {
            from: startDate,
            to: endDate,
          };
        }
      }
    }
    // Reset pagination and data when textSearch or date changes
    setPaginatedInfos((prev) => ({ ...prev, page: 1 }));
    setData([]);
    getBonusPaymentsData(dateToUse);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [textSearch]);

  useEffect(() => {
    if (dateReload) {
      // Reset pagination and data when date changes
      setPaginatedInfos((prev) => ({ ...prev, page: 1 }));
      setData([]);
      getBonusPaymentsData(date);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dateReload, textSearch]);

  // Function to update URL parameters when dates change
  const updateUrlParams = useCallback(
    (newDate: DateRange | undefined) => {
      const params = new URLSearchParams(searchParams);

      if (newDate?.from && newDate?.to) {
        params.set("startDate", newDate.from.toISOString());
        params.set("endDate", newDate.to.toISOString());
      } else {
        params.delete("startDate");
        params.delete("endDate");
      }

      setSearchParams(params);
    },
    [searchParams, setSearchParams]
  );

  return (
    <div className="w-full bg-white rounded-lg p-4 flex flex-col gap-4 max-h-[400px] overflow-hidden">
      <div className="flex justify-between items-center">
        <h5 className="font-semibold text-lg">Bonuszahlungen</h5>
        <DatePickerWithRange
          setDate={(date) => {
            setDate(date);
            if (date?.to && date?.from) {
              setDateReload(true);
              updateUrlParams(date);
            }
          }}
          date={date}
          className="w-auto"
        />
      </div>

      <div ref={scrollContainerRef} className="flex-1 overflow-y-auto">
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: UserProvisionListResponseDto[]) => (
            <div className="overflow-x-auto">
              <table className="w-full">
                {/* Table Header */}
                <thead>
                  <tr className="border-b border-gray-200">
                    <th className="text-left py-3 px-2 font-medium text-gray-700">Mitarbeiter</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-700">Ausgezahlter Bonus</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-700">Gesamtbonus</th>
                    <th className="text-right py-3 px-2 font-medium text-gray-700">Bonus Auswahl</th>
                    {isAdmin && <th className="text-right py-3 px-2 font-medium text-gray-700">Aktionen</th>}
                    <th className="w-12 py-3 px-2"></th>
                  </tr>
                </thead>

                {/* Table Body */}
                <tbody>
                  {data.map((user: UserProvisionListResponseDto) => (
                    <UserProvisionTableRow key={user._id} user={user} isAdmin={isAdmin} date={date} />
                  ))}
                </tbody>
              </table>

              {/* Loading indicator for infinite scroll */}
              {loadingMore && (
                <div className="flex justify-center py-4">
                  <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                </div>
              )}

              {/* End of data indicator */}
              {!paginatedInfos.hasNextPage && data.length > 0 && (
                <div className="text-center py-4 text-gray-500 text-sm">Alle Daten geladen</div>
              )}
            </div>
          )}
        />
      </div>
    </div>
  );
};

const UserProvisionTableRow = ({
  user,
  isAdmin,
  date,
}: {
  user: UserProvisionListResponseDto;
  isAdmin: boolean;
  date: DateRange | undefined;
}) => {
  const [open, setOpen] = useState(false);
  const [showPayoutDialog, setShowPayoutDialog] = useState(false);
  const [showPayoutHistoryDialog, setShowPayoutHistoryDialog] = useState(false);

  return (
    <>
      {/* Main Row */}
      <tr
        className={`border-b border-gray-100 hover:bg-gray-50 transition-colors cursor-pointer ${
          user.isArchive ? "bg-gray-100" : ""
        }`}
        onClick={() => setOpen(!open)}
      >
        <td className="py-2 px-2 flex items-center gap-4">
          <UserProfileRow
            profilePictureUrl={user.profilePictureUrl}
            name={user.firstName + " " + user.lastName}
            email={""}
          />
        </td>
        <td className="py-2 px-2 text-right font-medium text-gray-900">
          {user.paidOutProvision.toLocaleString("de-DE")}€
        </td>
        <td className="py-2 px-2 text-right font-medium text-gray-900">
          {user.sumProvisionAllTime.toLocaleString("de-DE")}€
        </td>
        <td className="py-2 px-2 text-right font-medium text-gray-900">
          {user.sumProvisionSelectedPeriod.toLocaleString("de-DE")} €
        </td>
        {isAdmin && (
          <td className="py-2 px-2">
            <div className="flex items-center justify-end gap-2">
              <Button
                size="sm"
                asChild
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPayoutDialog(true);
                }}
              >
                <div className="flex items-center">
                  <Plus className="w-4 h-4" />
                </div>
              </Button>
              <Button
                size="sm"
                asChild
                variant="outline"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowPayoutHistoryDialog(true);
                }}
              >
                <div className="flex items-center">
                  <Info className="w-4 h-4" />
                </div>
              </Button>
            </div>
          </td>
        )}
        <td className="py-2 px-2">
          <div className="flex items-center justify-center">
            <ChevronDown
              className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
            />
          </div>
        </td>
      </tr>

      {/* Organization Rows */}
      {open && <UserProvisionOrganizationRows userId={user._id} isAdmin={isAdmin} date={date} />}

      {/* Dialogs */}
      <PayoutDialog open={showPayoutDialog} onClose={() => setShowPayoutDialog(false)} user={user} />
      <PayoutHistoryDialog
        open={showPayoutHistoryDialog}
        onClose={() => setShowPayoutHistoryDialog(false)}
        user={user}
      />
    </>
  );
};

const UserProvisionOrganizationRows = ({
  userId,
  isAdmin,
  date,
}: {
  userId: string;
  isAdmin: boolean;
  date: DateRange | undefined;
}) => {
  const { apiRequest } = useApiRequest();
  const [userProvisionOrganizations, setUserProvisionOrganizations] = useState<UserProvisionOrganizationsResponseDto>();
  const [loading, setLoading] = useState(true);
  const [selectedOrganizationId, setSelectedOrganizationId] = useState<string | undefined>(undefined);

  const getUserProvisionOrganizations = useCallback(async () => {
    setLoading(true);
    let url = `provision/admin/internal-user-provisions/${userId}`;

    if (date?.from && date?.to) {
      url += `?startDate=${date.from?.toISOString()}&endDate=${date.to?.toISOString()}`;
    }

    const response = await apiRequest<UserProvisionOrganizationsResponseDto>(url, "GET");

    if (response.data) {
      setUserProvisionOrganizations(response.data);
    }
    setLoading(false);
  }, [userId, apiRequest, date]);

  useEffect(() => {
    getUserProvisionOrganizations();
  }, [getUserProvisionOrganizations]);

  if (loading) {
    return (
      <tr>
        <td colSpan={isAdmin ? 6 : 5} className="py-2 px-8">
          <div className="text-center text-gray-500 py-4">Lade Organisationen...</div>
        </td>
      </tr>
    );
  }

  if (!userProvisionOrganizations || userProvisionOrganizations.userProvisionOrganizations.length === 0) {
    return (
      <tr>
        <td colSpan={isAdmin ? 6 : 5} className="py-2 px-8">
          <div className="text-center text-gray-500 py-4">Keine Kunden verfügbar</div>
        </td>
      </tr>
    );
  }

  return (
    <>
      {userProvisionOrganizations.userProvisionOrganizations.map(
        (customer: UserProvisionOrganizationsListResponseDto) => {
          return (
            <tr
              key={customer.belongsToOrganizationId}
              className={`rounded-lg bg-main-bg border-b border-gray-100 ${
                isAdmin ? "hover:bg-blue-50 transition-colors cursor-pointer" : ""
              }`}
              onClick={() => {
                if (isAdmin) {
                  setSelectedOrganizationId(customer.belongsToOrganizationId);
                }
              }}
            >
              <td colSpan={2} className="py-2 px-2">
                <div className="flex items-center gap-4 pl-8">
                  <span className="font-medium text-gray-700">{customer.belongsToOrganizationName}</span>
                </div>
              </td>
              <td className="py-2 px-2 text-right font-medium text-gray-900">
                {customer.sumProvisionAllTime.toLocaleString("de-DE")}€
              </td>
              <td className="py-2 px-2 text-right font-medium text-gray-900">
                {customer.sumProvisionSelectedPeriod.toLocaleString("de-DE")}€
              </td>
              {isAdmin && (
                <td className="py-2 px-2">
                  <div className="flex items-center justify-end gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedOrganizationId(customer.belongsToOrganizationId);
                      }}
                    >
                      <Info className="w-4 h-4" />
                    </Button>
                  </div>
                </td>
              )}
              <td className="py-2 px-2"></td>
            </tr>
          );
        }
      )}
      <OrganisationDetailsPopup
        organizationId={selectedOrganizationId}
        userId={userId}
        onClose={() => setSelectedOrganizationId(undefined)}
      />
    </>
  );
};

const OrganisationDetailsPopup = ({
  organizationId,
  userId,
  onClose,
}: {
  organizationId?: string;
  userId: string;
  onClose: () => void;
}) => {
  const { apiRequest } = useApiRequest();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<UserProvisionHistoryListResponseDto>();

  const getUserProvisionHistory = useCallback(async () => {
    if (!organizationId) {
      return;
    }
    setLoading(true);
    const response = await apiRequest<UserProvisionHistoryListResponseDto>(
      `provision/admin/internal-user-provisions/${userId}/${organizationId}`,
      "GET"
    );
    if (response.data) {
      setData(response.data);
    }
    setLoading(false);
  }, [organizationId, userId, apiRequest]);

  useEffect(() => {
    getUserProvisionHistory();
  }, [getUserProvisionHistory]);

  return (
    <Dialog open={!!organizationId} onOpenChange={() => onClose()}>
      <DialogContent className="max-w-4xl h-[80vh] overflow-hidden">
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: UserProvisionHistoryListResponseDto) => (
            <div className="h-full flex flex-col overflow-y-auto custom-scrollbar">
              <DialogHeader className="pb-6">
                <DialogTitle>
                  <div className="flex items-center gap-4 mb-4">
                    <div className="flex items-center gap-2">
                      <TrendingUp className="w-6 h-6 text-blue-600" />
                      <h2 className="text-2xl font-bold text-gray-900">Provisionshistorie</h2>
                    </div>
                  </div>
                </DialogTitle>

                {/* Header Summary */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <UserProfileRow
                        profilePictureUrl={data.user.profilePictureUrl}
                        name={`${data.user.firstName} ${data.user.lastName}`}
                        email=""
                      />
                    </div>
                  </div>

                  <div className="bg-gray-50 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <MapPin className="w-6 h-6 text-gray-600" />
                        <div>
                          <p className="text-sm text-gray-600">Unternehmen</p>
                          <p className="font-semibold text-gray-900">{data.belongsToOrganizationName}</p>
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm text-gray-600">Gesamtprovision</p>
                        <p className="text-xl font-bold text-gray-900">
                          {data.totalProvisionAmount.toLocaleString("de-DE")}€
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </DialogHeader>

              {/* Timeline Content */}
              <div className="flex-1 overflow-y-auto custom-scrollbar pr-2">
                <div className="relative">
                  <h3 className="text-lg font-semibold text-gray-900 mb-6 flex items-center gap-2">
                    <Clock className="w-5 h-5" />
                    Zuweisungsperioden
                  </h3>

                  {data.assignmentPeriodChains.length > 0 ? (
                    <div className="space-y-6">
                      {data.assignmentPeriodChains.map((chain, index) => (
                        <AssignmentPeriodCard
                          key={index}
                          chain={chain}
                          isLast={index === data.assignmentPeriodChains.length - 1}
                          index={index}
                        />
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-12">
                      <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                      <p className="text-gray-500">Keine Zuweisungsperioden gefunden</p>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

const AssignmentPeriodCard: React.FC<{
  chain: AssignmentPeriodChainListResponseDto;
  isLast: boolean;
  index: number;
}> = ({ chain, isLast, index }) => {
  const { provisions, assignmentPeriodData } = chain;
  const isActive = assignmentPeriodData?.isActive;
  const assignmentPeriodChainItem: AssignmentPeriodListResponseDto | undefined = chain.assignmentPeriodChain[0];
  const [isProvisionsOpen, setIsProvisionsOpen] = useState(false);
  const [isAssignmentPeriodsOpen, setIsAssignmentPeriodsOpen] = useState(false);

  return (
    <div className="relative">
      {/* Timeline Line */}
      {!isLast && <div className="absolute left-[23px] w-0.5 h-full bg-gray-200 z-0"></div>}

      {/* Timeline Dot */}
      <div
        className={`absolute left-4 w-4 h-4 rounded-full border-4 z-10 ${
          isActive ? "bg-green-500 border-green-200" : "bg-gray-400 border-gray-200"
        }`}
      ></div>

      {/* Card Content */}
      <div className="ml-12 bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-shadow">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-3">
              <span className="text-sm font-medium text-gray-500">Zeitraum #{index + 1}</span>
              <div
                className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
                  isActive ? "bg-green-100 text-green-700" : "bg-gray-100 text-gray-700"
                }`}
              >
                {isActive ? (
                  <>
                    <CheckCircle2 className="w-3 h-3" />
                    Aktiv
                  </>
                ) : (
                  <>
                    <XCircle className="w-3 h-3" />
                    Beendet
                  </>
                )}
              </div>
            </div>

            {/* Provision Amount */}
            <div className="text-right">
              <div className="text-2xl font-bold text-green-600">
                {provisions.reduce((acc, provision) => acc + provision.amount, 0).toLocaleString("de-DE")}€
              </div>
              <div className="text-xs text-gray-500">
                {provisions.length} Provision{provisions.length !== 1 ? "en" : ""}
              </div>
            </div>
          </div>

          {/* Individual Provisions */}
          {provisions.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setIsProvisionsOpen(!isProvisionsOpen)}
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900 transition-colors"
              >
                <Euro className="w-4 h-4" />
                Provisionen ({provisions.length})
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${isProvisionsOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isProvisionsOpen ? "max-h-44 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                  {provisions.map((provision, provisionIndex) => (
                    <div
                      key={provisionIndex}
                      className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg p-3 border border-green-200"
                    >
                      <div className="flex items-center justify-between mb-2">
                        <div className="text-lg font-bold text-green-700">
                          {provision.amount.toLocaleString("de-DE")}€
                        </div>
                        <div className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full font-medium">
                          Monat {provision.months}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 text-xs text-green-600">
                        <Calendar className="w-3 h-3" />
                        <span>{formatDate(provision.provisionDate)}</span>
                      </div>
                      {provision.note && (
                        <div className="flex items-center gap-1 text-xs text-green-600">
                          <FileText className="w-3 h-3" />
                          <span>{provision.note}</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Assignment Periods Accordion */}
          {chain.assignmentPeriodChain && chain.assignmentPeriodChain.length > 0 && (
            <div className="mb-4">
              <button
                onClick={() => setIsAssignmentPeriodsOpen(!isAssignmentPeriodsOpen)}
                className="flex items-center gap-2 text-sm font-semibold text-gray-700 mb-3 hover:text-gray-900 transition-colors"
              >
                <Clock className="w-4 h-4" />
                Zuweisungsperioden ({chain.assignmentPeriodChain.length})
                <ChevronDown
                  className={`w-4 h-4 transition-transform duration-200 ${isAssignmentPeriodsOpen ? "rotate-180" : ""}`}
                />
              </button>
              <div
                className={`overflow-y-auto transition-all duration-300 ease-in-out ${
                  isAssignmentPeriodsOpen ? "max-h-96 opacity-100" : "max-h-0 opacity-0"
                }`}
              >
                <div className="space-y-3">
                  {chain.assignmentPeriodChain.map((period, periodIndex) => (
                    <div key={periodIndex} className="bg-gray-50 rounded-lg p-3 border border-gray-200">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-sm font-medium text-gray-700">Zeitraum #{periodIndex + 1}</span>
                      </div>
                      <div className="space-y-2">
                        {period.assignedTimestamp && (
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-3 h-3 text-blue-500" />
                            <span className="text-gray-600">Zugewiesen:</span>
                            <span className="font-medium">{formatDate(period.assignedTimestamp)}</span>
                          </div>
                        )}
                        {period.unassignedTimestamp && (
                          <div className="flex items-center gap-2 text-sm">
                            <Calendar className="w-3 h-3 text-red-500" />
                            <span className="text-gray-600">Beendet:</span>
                            <span className="font-medium">{formatDate(period.unassignedTimestamp)}</span>
                            {period.closeReason && (
                              <>
                                <span className="text-gray-400">•</span>
                                <span className="font-medium text-red-600">
                                  {period.closeReason === "DUPLICATE" && "Duplikat"}
                                  {period.closeReason === "UNASSIGNMENT" && "Zuweisung entfernt"}
                                  {period.closeReason === "CANCELLATION" && "Kündigung"}
                                  {period.closeReason === "CAPACITY_CHANGE" && "Kapazitätsänderung"}
                                </span>
                              </>
                            )}{" "}
                          </div>
                        )}
                        {/* Capacity */}
                        <div className="flex items-center gap-2 text-sm">
                          <Users className="w-3 h-3 text-green-500" />
                          <span className="text-gray-600">Kapazität:</span>
                          <span className="font-medium">{period.organizationCapacity}</span>
                        </div>
                        {/* Notes */}
                        {period.note && (
                          <div className="space-y-2">
                            <div className="flex items-start gap-2">
                              <FileText className="w-3 h-3 text-yellow-500 mt-0.5" />
                              <div>
                                <span className="text-sm text-gray-600">Notiz:</span>
                                <p className="text-sm text-gray-900 mt-1 bg-blue-50 p-3 rounded-md">{period.note}</p>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          {assignmentPeriodChainItem && (
            <>
              {/* Timeline Details */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div className="space-y-2">
                  {assignmentPeriodData?.startDate && (
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      <span className="text-gray-600">Startdatum:</span>
                      <span className="font-medium">{formatDate(assignmentPeriodData?.startDate)}</span>
                    </div>
                  )}
                  <div className="flex items-center gap-2 text-sm">
                    <Clock className="w-4 h-4 text-purple-500" />
                    <span className="text-gray-600">Dauer:</span>
                    <span className="font-medium">
                      {assignmentPeriodData?.months} Monat{assignmentPeriodData?.months !== 1 ? "e" : ""}
                    </span>
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

const PayoutHistoryDialog = ({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: UserProvisionListResponseDto;
}) => {
  const { apiRequest } = useApiRequest();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<UserPaidOutProvisionListResponseDto>();

  const getUserProvisionHistory = useCallback(async () => {
    if (!user._id || !open) {
      return;
    }
    setLoading(true);
    const response = await apiRequest<UserPaidOutProvisionListResponseDto>(`provision/admin/payout/${user._id}`, "GET");
    if (response.data) {
      setData(response.data);
    }
    setLoading(false);
  }, [user._id, apiRequest, open]);

  useEffect(() => {
    getUserProvisionHistory();
  }, [getUserProvisionHistory]);

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            <div className="flex items-center gap-2">
              <Euro className="w-5 h-5" />
              Ausgezahlte Provisionen
            </div>
          </DialogTitle>
        </DialogHeader>
        <DataFetchParent
          isLoading={loading}
          data={data}
          renderElement={(data: UserPaidOutProvisionListResponseDto) => (
            <>
              <div className="flex flex-col gap-3">
                {data.paidOutProvisionList.length > 0 ? (
                  data.paidOutProvisionList.map((paidOutProvision) => {
                    const isNegative = paidOutProvision.amount < 0;
                    const isPositive = paidOutProvision.amount > 0;

                    return (
                      <div
                        key={paidOutProvision.createdAt}
                        className={`flex items-center justify-between p-3 rounded-lg border ${
                          isNegative
                            ? "bg-red-50 border-red-200"
                            : isPositive
                            ? "bg-green-50 border-green-200"
                            : "bg-gray-50 border-gray-200"
                        }`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-lg font-semibold ${
                              isNegative ? "text-red-600" : isPositive ? "text-green-600" : "text-gray-600"
                            }`}
                          >
                            {isNegative ? "−" : isPositive ? "+" : ""}
                            {Math.abs(paidOutProvision.amount).toLocaleString("de-DE")}€
                          </span>
                          <span
                            className={`text-xs px-2 py-1 rounded-full font-medium ${
                              isNegative
                                ? "bg-red-100 text-red-700"
                                : isPositive
                                ? "bg-green-100 text-green-700"
                                : "bg-gray-100 text-gray-700"
                            }`}
                          >
                            {isNegative ? "Korrektur" : isPositive ? "Auszahlung" : "Neutral"}
                          </span>
                        </div>
                        <div className="text-sm text-gray-500 flex items-center gap-1">
                          <Calendar className="w-3 h-3" />
                          {formatDate(paidOutProvision.createdAt)}
                        </div>
                      </div>
                    );
                  })
                ) : (
                  <div className="text-center py-12">Keine Auszahlungen gefunden</div>
                )}
              </div>
            </>
          )}
        />
      </DialogContent>
    </Dialog>
  );
};

// Payout form schema
const payoutFormSchema = z.object({
  amount: z
    .string()
    .min(1, { message: "Betrag ist erforderlich" })
    .refine((val) => !isNaN(Number(val)), {
      message: "Betrag muss eine Zahl sein",
    })
    .refine((val) => Number(val) !== 0, {
      message: "Betrag darf nicht 0 sein",
    })
    .transform((val) => Number(val)),
});

type PayoutFormValues = z.infer<typeof payoutFormSchema>;

// Payout Dialog Component
const PayoutDialog = ({
  open,
  onClose,
  user,
}: {
  open: boolean;
  onClose: () => void;
  user: UserProvisionListResponseDto;
}) => {
  const { apiRequest } = useApiRequest();
  const availableAmount = user.sumProvisionAllTime - user.paidOutProvision;

  const form = useForm<PayoutFormValues>({
    resolver: zodResolver(payoutFormSchema),
    mode: "onChange",
    defaultValues: {
      amount: 0,
    },
  });

  const { isSubmitting } = form.formState;

  const onSubmit = async (data: PayoutFormValues) => {
    try {
      const payoutData: PayOutProvisionRequestDto = {
        belongsToInternalUserId: user._id,
        amount: data.amount,
      };

      const response = await apiRequest(`provision/admin/payout`, "POST", {
        body: payoutData,
      });

      if (response.data) {
        // Success - could show a success message here
        onClose();
        form.reset();
        // Refresh the page or update the data
        window.location.reload();
      }
    } catch (error) {
      console.error("Error processing payout:", error);
    }
  };

  const handleClose = () => {
    form.reset();
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Euro className="w-5 h-5" />
            Provision auszahlen
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <UserProfileRow
                  profilePictureUrl={user.profilePictureUrl}
                  name={`${user.firstName} ${user.lastName}`}
                  email=""
                />
              </div>
              <div className="mt-3 text-sm text-gray-600">
                <div className="flex justify-between">
                  <span>Offene Provision:</span>
                  <span className="font-semibold">{availableAmount.toLocaleString("de-DE")}€</span>
                </div>
              </div>
            </div>

            <FormField
              control={form.control}
              name="amount"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Auszuzahlender Betrag (€)</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      step="0.01"
                      placeholder="Betrag eingeben... (negative Werte für Korrekturen erlaubt)"
                      {...field}
                      onChange={(e) => field.onChange(e.target.value)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="flex gap-2 pt-4">
              <Button variant="outline" size="sm" onClick={handleClose} className="flex-1">
                Abbrechen
              </Button>
              <Button type="submit" disabled={isSubmitting} size="sm" className="flex-1">
                {isSubmitting ? "Wird verarbeitet..." : "Auszahlen"}
              </Button>
            </div>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default BonusPayments;
